import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import MeInfo from "./MeInfo";
import { getMyProfile } from "../api/me";
import { Me } from "../types/Me";
import { testSessionId } from "../api/api";
import { breakpoints } from "../styles/theme";

import { ReactComponent as HomeIcon } from "../assets/icons/home.svg";
import { ReactComponent as SearchIcon } from "../assets/icons/search.svg";
import { ReactComponent as NotificationIcon } from "../assets/icons/notification.svg";
import { ReactComponent as MessagesIcon } from "../assets/icons/messages.svg";
import { ReactComponent as SettingsIcon } from "../assets/icons/settings.svg";

const Sidebar: React.FC = () => {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMe = async () => {
      if (!testSessionId) {
        setLoading(false);
        return;
      }
      try {
        const myProfile = await getMyProfile();
        setMe(myProfile);
      } catch (error) {
        console.error("Failed to fetch user profile:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchMe();
  }, []);

  return (
    <SidebarContainer>
      <Header>
        <Logo src="/logo.svg" alt="Atten logo" />
      </Header>
      <MenuItem>
        <SidebarLink href="/">
          <IconContainer>
            <HomeIcon className="sidebar-icon" />
            <LinkLabel>ホーム</LinkLabel>
          </IconContainer>
        </SidebarLink>
      </MenuItem>

      <MenuItem>
        <StyledLink to="/search">
          <IconContainer>
            <SearchIcon className="sidebar-icon" />
            <LinkLabel>検索</LinkLabel>
          </IconContainer>
        </StyledLink>
      </MenuItem>

      <MenuItem>
        <SidebarLink href="/notifications">
          <IconContainer>
            <NotificationIcon className="sidebar-icon" />
            <LinkLabel>通知</LinkLabel>
          </IconContainer>
        </SidebarLink>
      </MenuItem>

      <MenuItem>
        <StyledLink to="/settings">
          <IconContainer>
            <MessagesIcon className="sidebar-icon" />
            <LinkLabel>メッセージ</LinkLabel>
          </IconContainer>
        </StyledLink>
      </MenuItem>

      <MenuItem>
        <StyledLink to="/settings">
          <IconContainer>
            <SettingsIcon className="sidebar-icon" />
            <LinkLabel>設定</LinkLabel>
          </IconContainer>
        </StyledLink>
      </MenuItem>

      <Spacer />
      {loading ? (
        <p>ロード中...</p>
      ) : me ? (
        <MeInfo user={me} />
      ) : (
        <p>ログインしてください。</p>
      )}
    </SidebarContainer>
  );
};

const SidebarContainer = styled.aside`
  display: flex;
  flex-direction: column;
  width: 350px;
  height: 100vh;
  border-right: 1px solid #eeeeee;
  position: sticky;
  top: 0;
  left: 0;
  background: #fff;
  z-index: 10;

  @media (max-width: ${breakpoints.mobile}) {
    display: none; // スマホでは非表示
  }
`;

const Header = styled.div`
  font-weight: 700;
  font-size: 36px;
  margin-bottom: 40px;
  text-align: center;
`;

const Logo = styled.img`
  height: 72px;
  display: block;
  margin: 32px auto 0 auto;
`;

const MenuItem = styled.div`
  width: 100%;
  margin-bottom: 12px;
  display: flex;
`;

const IconContainer = styled.div`
  background: rgb(255, 255, 255);
  border-radius: 50px;
  width: 200px;
  height: 50px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: flex-start; /* ← アイコンと文字を横並びに左寄せ */
  padding: 0 16px;
  gap: 12px; /* ← アイコンと文字の間のスペース */
  transition: background-color 0.2s ease, box-shadow 0.2s ease;

  .sidebar-icon {
    width: 22px;
    height: 22px;
  }

  &:hover {
    background: rgb(245, 245, 245);
    border-color: #cccccc;
    box-shadow: 0 0px 10px rgba(0, 0, 0, 0.03);
  }
`;

const LinkLabel = styled.span`
  font-weight: 500;
  font-size: 20px;
  color: #333333;
  margin-left: 10px;
`;

const SidebarLink = styled.a`
  text-decoration: none;
  color: inherit;
  display: flex;
  align-items: center;
  width: 100%;
`;

const StyledLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  display: flex;
  align-items: center;
  width: 100%;
`;

const Spacer = styled.div`
  flex-grow: 1;
`;

export default Sidebar;
